import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@src/auth/AuthContext';
import { useTerminalAccount } from '@src/context/AccountContext';
import { fetchPunchMapping } from '@src/api/punchMappingApi';

export type ExternalUserStatus =
    | 'idle'
    | 'loading'
    | 'resolved'
    | 'unresolved';

export interface ExternalUserContextValue {
    /** The external user ID currently being resolved, or null if not in external-user mode. */
    externalUserId: string | null;
    /** The integration application name, sourced from the terminal account (init-scoped). */
    applicationName: string | null;
    /** The resolved employee ID (PIN) once status is `resolved`. */
    resolvedEmployeeId: string | null;
    /** Resolution status. */
    status: ExternalUserStatus;
    /**
     * Monotonically increasing id of the current external-user request. Bumped
     * on every `setExternalUser` call — including a repeat of the same
     * `externalUserId` — so consumers can treat each `set_external_user`
     * message as a distinct request (e.g. to re-trigger the PIN-pad auto-skip).
     */
    requestId: number;
    /**
     * Set or update the active external user (from the init payload or a
     * runtime `set_external_user` message). The integration `applicationName`
     * is sourced from the terminal account, not this call, so external-user
     * mode works even when the terminal booted in PIN mode (no init
     * `externalUserId`). Always starts a fresh resolution, even when the
     * `externalUserId` matches the previous one.
     */
    setExternalUser: (params: { externalUserId: string }) => void;
    /** Reset all external user state back to idle (call after session exit). */
    reset: () => void;
}

const ExternalUserContext = createContext<ExternalUserContextValue | null>(
    null
);

export function ExternalUserProvider({ children }: { children: ReactNode }) {
    const { accessToken } = useAuth();
    const { account } = useTerminalAccount();

    const [externalUserId, setExternalUserId] = useState<string | null>(null);
    // Bumped on every setExternalUser call so a repeat of the same
    // externalUserId still starts a fresh resolution — a set_external_user
    // message is an explicit host command to (re)enter external mode, even for
    // the user already resolved (API-4790).
    const [requestId, setRequestId] = useState(0);
    // Sourced from the terminal account (always set at init, since
    // `isInitPayload` requires `applicationName`) rather than captured from the
    // `setExternalUser` call. This is what lets a runtime `set_external_user`
    // message resolve the user on a terminal that booted in PIN mode, where no
    // init `externalUserId` ever seeded a local applicationName (API-4790).
    const applicationName = account?.applicationName ?? null;
    const [status, setStatus] = useState<ExternalUserStatus>('idle');
    const [resolvedEmployeeId, setResolvedEmployeeId] = useState<string | null>(
        null
    );

    // Track in-flight fetch so stale responses from a previous externalUserId
    // are discarded when setExternalUser is called again.
    const fetchTokenRef = useRef<symbol | null>(null);

    useEffect(() => {
        if (!externalUserId || !applicationName || !account || !accessToken) {
            return;
        }

        const token = Symbol('fetchPunchMapping');
        fetchTokenRef.current = token;
        setStatus('loading');
        setResolvedEmployeeId(null);

        fetchPunchMapping(
            account.companyId,
            applicationName,
            account.locationId,
            externalUserId,
            accessToken
        )
            .then(result => {
                if (fetchTokenRef.current !== token) return;
                setResolvedEmployeeId(result.employee_id);
                setStatus('resolved');
            })
            .catch(() => {
                if (fetchTokenRef.current !== token) return;
                setStatus('unresolved');
            });
    }, [externalUserId, applicationName, account, accessToken, requestId]);

    const setExternalUser = useCallback((params: { externalUserId: string }) => {
        setExternalUserId(params.externalUserId);
        setRequestId(prev => prev + 1);
        setStatus('idle');
        setResolvedEmployeeId(null);
    }, []);

    const reset = useCallback(() => {
        setExternalUserId(null);
        setStatus('idle');
        setResolvedEmployeeId(null);
        fetchTokenRef.current = null;
    }, []);

    return (
        <ExternalUserContext.Provider
            value={{
                externalUserId,
                applicationName,
                resolvedEmployeeId,
                status,
                requestId,
                setExternalUser,
                reset
            }}
        >
            {children}
        </ExternalUserContext.Provider>
    );
}

export function useExternalUser(): ExternalUserContextValue {
    const ctx = useContext(ExternalUserContext);
    if (!ctx) {
        throw new Error(
            'useExternalUser must be used within an ExternalUserProvider'
        );
    }
    return ctx;
}
